// VENDORED COPY of frontend/src/lib/financial/kpis.ts — do not edit here; keep in sync with the main app.
/**
 * Financial KPI calculations.
 *
 * Ported from static/apps/lib/financial-calc.js to eliminate the
 * window.AppLib dependency. All functions are exact logic mirrors
 * of the legacy implementations.
 */

import type { HorizonResult, KPIResult, BreakEvenOptions } from './types';

/**
 * Calculate ROI (Return on Investment) percentage.
 * Formula: ((totalValue - totalCost) / totalCost) * 100
 *
 * @param totalValue - Total value / savings
 * @param totalCost - Total cost
 * @returns ROI percentage (Infinity if cost is 0 but value > 0)
 */
export function calculateROI(totalValue: number, totalCost: number): number {
  if (totalCost === 0) return totalValue > 0 ? Infinity : 0;
  return ((totalValue - totalCost) / totalCost) * 100;
}

/**
 * Calculate Net Value (value minus cost).
 *
 * @param totalValue - Total value / savings
 * @param totalCost - Total cost
 * @returns Net value
 */
export function calculateNetValue(totalValue: number, totalCost: number): number {
  return totalValue - totalCost;
}

/**
 * Find the break-even month where cumulative value exceeds cumulative cost.
 *
 * @param valueMonthly - Monthly value / savings array
 * @param costMonthly - Monthly cost array
 * @param options - One-time cost/value offsets
 * @returns Break-even month (1-indexed), or null if not reached
 */
export function findBreakEvenMonth(
  valueMonthly: number[],
  costMonthly: number[],
  options: BreakEvenOptions = {},
): number | null {
  const { oneTimeCost = 0, oneTimeValue = 0 } = options;

  if (!valueMonthly || !costMonthly) return null;
  if (valueMonthly.length === 0 && costMonthly.length === 0) return null;

  const maxLength = Math.max(valueMonthly.length, costMonthly.length);
  let cumulativeValue = oneTimeValue;
  let cumulativeCost = oneTimeCost;

  // Check month 0 -- if value already >= cost at start
  if (cumulativeValue >= cumulativeCost && cumulativeCost > 0) {
    return 0;
  }

  for (let month = 0; month < maxLength; month++) {
    cumulativeValue += valueMonthly[month] || 0;
    cumulativeCost += costMonthly[month] || 0;

    if (cumulativeValue >= cumulativeCost && cumulativeCost > 0) {
      return month + 1; // 1-indexed month number
    }
  }

  return null; // No break-even within the horizon
}

/**
 * Calculate payback period in months.
 * Similar to break-even but returns fractional months for more precision.
 *
 * @param valueMonthly - Monthly value / savings array
 * @param costMonthly - Monthly cost array
 * @param options - One-time cost/value offsets
 * @returns Payback period in months (can be fractional), or null if not reached
 */
export function calculatePaybackPeriod(
  valueMonthly: number[],
  costMonthly: number[],
  options: BreakEvenOptions = {},
): number | null {
  const { oneTimeCost = 0, oneTimeValue = 0 } = options;

  if (!valueMonthly || !costMonthly) return null;

  const maxLength = Math.max(valueMonthly.length, costMonthly.length);
  let cumulativeValue = oneTimeValue;
  let cumulativeCost = oneTimeCost;
  let prevNetValue = cumulativeValue - cumulativeCost;

  // Check month 0
  if (prevNetValue >= 0 && cumulativeCost > 0) {
    return 0;
  }

  for (let month = 0; month < maxLength; month++) {
    cumulativeValue += valueMonthly[month] || 0;
    cumulativeCost += costMonthly[month] || 0;
    const netValue = cumulativeValue - cumulativeCost;

    if (netValue >= 0 && cumulativeCost > 0) {
      // Interpolate to find fractional month
      if (prevNetValue < 0 && netValue >= 0) {
        const fraction = Math.abs(prevNetValue) / (Math.abs(prevNetValue) + netValue);
        return month + fraction;
      }
      return month;
    }

    prevNetValue = netValue;
  }

  return null;
}

/**
 * Calculate Net Present Value (NPV) of a project with a constant annual
 * cashflow and a single up-front CAPEX outflow.
 *
 * Formula:  NPV = Σ_{t=1..H} CF / (1 + r)^t − CAPEX
 * where    r = discountRatePct / 100.
 *
 * Edge cases:
 *   - discountRatePct === -100 (r === -1) produces a divide-by-zero
 *     denominator and returns `NaN`. Callers must guard with
 *     `Number.isFinite()` before formatting for display.
 *   - horizonYears is treated as the literal value the caller passes in.
 *     Callers should clamp to a sensible positive integer (e.g.
 *     `Math.max(1, Math.floor(value))`). Passing `0` returns `-capex`
 *     (no future cashflows); passing a negative horizon returns
 *     `-capex` as well (empty sum).
 *
 * Pure: no globals, no DOM, no side effects. Mirrors the idiom of
 * `calculateROI` and `calculateNetValue` so the library stays cohesive.
 *
 * @param annualCashflow - Constant annual cashflow `CF` (positive for net inflow)
 * @param capex - One-time up-front investment (subtracted at t=0)
 * @param discountRatePct - Discount rate as a percentage (e.g. `9` for 9%)
 * @param horizonYears - Number of future years to discount
 * @returns NPV in the same units as `annualCashflow`, or `NaN` when
 *          the denominator collapses (`discountRatePct === -100`).
 */
export function calculateNPV(
  annualCashflow: number,
  capex: number,
  discountRatePct: number,
  horizonYears: number,
): number {
  const r = discountRatePct / 100;
  if (r === -1) return NaN; // (1 + r)^t === 0 → division by zero
  let pv = 0;
  for (let t = 1; t <= horizonYears; t++) {
    pv += annualCashflow / Math.pow(1 + r, t);
  }
  return pv - capex;
}

/**
 * Calculate all financial KPIs from horizon projection data.
 * Convenience function that calculates all KPIs at once.
 *
 * @param valueHorizon - Result from calculateHorizon for savings / value
 * @param costHorizon - Result from calculateHorizon for costs
 * @returns Complete KPI result
 */
export function calculateKPIs(
  valueHorizon: HorizonResult | null | undefined,
  costHorizon: HorizonResult | null | undefined,
): KPIResult {
  const totalValue = valueHorizon?.total || 0;
  const totalCost = costHorizon?.total || 0;

  return {
    totalValue,
    totalCost,
    netValue: calculateNetValue(totalValue, totalCost),
    roi: calculateROI(totalValue, totalCost),
    breakEvenMonth: findBreakEvenMonth(
      valueHorizon?.monthly || [],
      costHorizon?.monthly || [],
    ),
    paybackPeriod: calculatePaybackPeriod(
      valueHorizon?.monthly || [],
      costHorizon?.monthly || [],
    ),
  };
}
